import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { WorkshopAssignedHolderTariffs, WorkshopAvailableSeats } from '@products/models/workshop-status.model';
import { WorkshopBookingService } from '@products/services/workshop-booking.service';
import { BookingActions } from '@store/products/booking/booking.actions';
import { WorkshopBookingActions } from '@store/products/booking/product/workshop/workshop-booking.actions';
import { WorkshopProductActions } from '@store/products/product.actions';
import { WorkshopStatusActions } from '@store/products/status/workshop/workshop-status.actions';
import { getWorkshopsAssignedHolderTariffsGroupedByWorkshopId } from '@store/products/status/workshop/workshop-status.selectors';
import { WorkshopActions } from '@store/products/workshop/workshop.actions';
import { difference, forEach, intersection, isEmpty, keys } from 'lodash';
import { catchError, concatMap, EMPTY, map, of, switchMap } from 'rxjs';

export const setInitialWorkshopAvailableSeatsStatus$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(WorkshopActions.setInitialWorkshopProductList),
      switchMap(({ workshopProductList }) => {
        if (isEmpty(workshopProductList)) {
          return EMPTY;
        }

        const workshopAvailableSeats: WorkshopAvailableSeats = workshopProductList.reduce(
          (workshopAvailableSeats, workshop) => {
            workshopAvailableSeats[workshop.workshopId] = workshop.seats;
            return workshopAvailableSeats;
          },
          {}
        );

        return of(WorkshopStatusActions.setWorkshopAvailableSeatsStatus(workshopAvailableSeats));
      })
    ),
  { functional: true }
);

export const setWorkshopAvailableSeatsStatus$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(WorkshopBookingActions.setWorkshopInBooking, WorkshopBookingActions.removeWorkshopFromBooking),
      switchMap(({ workshopAvailableSeats }) => {
        if (isEmpty(workshopAvailableSeats)) {
          return EMPTY;
        }

        return of(WorkshopStatusActions.setWorkshopAvailableSeatsStatus(workshopAvailableSeats));
      })
    ),
  { functional: true }
);

export const setWorkshopAssignedHolderTariffsStatusOnSetWorkshopBooking$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(WorkshopBookingActions.setWorkshopInBooking),
      concatLatestFrom(() => store.select(getWorkshopsAssignedHolderTariffsGroupedByWorkshopId)),
      switchMap(([{ setBookingWorkshops }, workshopsAssignedHolderTariffsGroupedByWorkshopId]) => {
        if (isEmpty(setBookingWorkshops)) {
          return EMPTY;
        }

        const workshopAssignedHolderTariffs: WorkshopAssignedHolderTariffs = {};

        setBookingWorkshops.forEach((setWorkshopBooking) =>
          setWorkshopBooking.workshops.forEach(({ id, tariffUuids }) => {
            const assignedWorkshopHolderTariffs = workshopsAssignedHolderTariffsGroupedByWorkshopId[id];

            if (workshopAssignedHolderTariffs[id]) {
              workshopAssignedHolderTariffs[id] = [...workshopAssignedHolderTariffs[id], ...tariffUuids];
            } else if (assignedWorkshopHolderTariffs) {
              workshopAssignedHolderTariffs[id] = [...assignedWorkshopHolderTariffs, ...tariffUuids];
            } else {
              workshopAssignedHolderTariffs[id] = tariffUuids;
            }
          })
        );

        return of(WorkshopStatusActions.setWorkshopAssignedHolderTariffsStatus(workshopAssignedHolderTariffs));
      })
    ),
  { functional: true }
);

export const setWorkshopAssignedHolderTariffsStatusOnRemoveWorkshopBooking$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(WorkshopBookingActions.removeWorkshopFromBooking),
      concatLatestFrom(() => store.select(getWorkshopsAssignedHolderTariffsGroupedByWorkshopId)),
      switchMap(([{ removeBookingWorkshops }, workshopsAssignedHolderTariffsGroupedByWorkshopId]) => {
        if (isEmpty(removeBookingWorkshops)) {
          return EMPTY;
        }

        const workshopAvailableSeats: WorkshopAssignedHolderTariffs = {};

        removeBookingWorkshops.forEach((removeBookingWorkshop) =>
          removeBookingWorkshop.removeWorkshopsAssignedHolderTariffs.forEach(({ workshopId, removeTariffUuids }) => {
            const assignedWorkshopHolderTariffs = workshopsAssignedHolderTariffsGroupedByWorkshopId[workshopId];
            workshopAvailableSeats[workshopId] = difference(assignedWorkshopHolderTariffs, removeTariffUuids);
          })
        );

        return of(WorkshopStatusActions.setWorkshopAssignedHolderTariffsStatus(workshopAvailableSeats));
      })
    ),
  { functional: true }
);

export const releaseAssignedWorkshopHolderSeatsOnProductHolderRemoval$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), workshopBookingService = inject(WorkshopBookingService)) =>
    actions$.pipe(
      ofType(BookingActions.removeBookedTariffUuids),
      concatLatestFrom(() => [store.select(getWorkshopsAssignedHolderTariffsGroupedByWorkshopId)]),
      concatMap(([{ bookedTariffUuids }, workshopsAssignedHolderTariffsGroupedByWorkshopId]) => {
        const workshopAssignedHolderTariffs: WorkshopAssignedHolderTariffs = {};
        const releaseWorkshopAssignedHolderTariffs: WorkshopAssignedHolderTariffs = {};
        const assignedHolderTariffsWorkshopsIds = keys(workshopsAssignedHolderTariffsGroupedByWorkshopId);

        forEach(assignedHolderTariffsWorkshopsIds, (workshopId) => {
          const assignedWorkshopHolderTariffs = workshopsAssignedHolderTariffsGroupedByWorkshopId[workshopId];

          workshopAssignedHolderTariffs[workshopId] = difference(assignedWorkshopHolderTariffs, bookedTariffUuids);
          releaseWorkshopAssignedHolderTariffs[workshopId] = intersection(
            assignedWorkshopHolderTariffs,
            bookedTariffUuids
          );
        });

        return workshopBookingService
          .postReleaseAssignedWorkshopHolderSeats$(workshopAssignedHolderTariffs, releaseWorkshopAssignedHolderTariffs)
          .pipe(
            map(({ workshopAssignedHolderTariffs, workshopAvailableSeats }) =>
              WorkshopStatusActions.setWorkshopAvailableSeatsAndAssignedHolderTariffsStatus({
                availableSeats: workshopAvailableSeats,
                assignedHolderTariffs: workshopAssignedHolderTariffs
              })
            ),
            catchError(() => of(WorkshopStatusActions.setWorkshopAvailableSeatsAndAssignedHolderTariffsStatus(null)))
          );
      })
    ),
  { functional: true }
);

export const isWorkshopLoadingStatus$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        WorkshopProductActions.setBookingWorkshop,
        WorkshopProductActions.removeBookingWorkshop,
        WorkshopBookingActions.setWorkshopInBooking,
        WorkshopBookingActions.removeWorkshopFromBooking
      ),
      map((action) => {
        const productActionTypes: string[] = [
          WorkshopProductActions.setBookingWorkshop.type,
          WorkshopProductActions.removeBookingWorkshop.type
        ];

        const isWorkshopLoading = productActionTypes.includes(action.type);

        return WorkshopStatusActions.setIsWorkshopLoadingStatus(isWorkshopLoading);
      })
    ),
  { functional: true }
);
